// Modules whose records fill templates (backend docs/template-data.md).
//
// docs' API only says which modules this project turned on and where they
// answer (GET /embed/v1/template-sources). The record picker, the field
// catalogue and the values come straight from the module, with the person's
// own token for it (embedTokenManager.getModuleToken) — so nobody sees a value
// that module would not show them. Plain fetch, not docs' axios stack: its
// 401 handling would clear docs' own session for a refusal by another module,
// and humps would camelize the field paths. Bodies stay snake_case.

import { auth } from './auth';
import baseAxios from './request';
import { getModuleToken } from './embedTokenManager';
import { TEMPLATE_SOURCES } from './urls';

let cached = null; // promise of [{source, apiUrl}] for this tab

/** The enabled sources, read once per tab (a failed read is retried next time). */
export function fetchTemplateSources({ refresh = false } = {}) {
  if (!cached || refresh) {
    cached = Promise.resolve()
      .then(() => baseAxios({ method: 'GET', url: TEMPLATE_SOURCES, headers: auth().headers }))
      .then((res) => (Array.isArray(res?.data?.items) ? res.data.items : []))
      .catch(() => {
        cached = null;
        return [];
      });
  }
  return cached;
}

/** `zehn-hr:employee` → {source: 'zehn-hr', entity: 'employee'} (null when not one). */
export function splitEntity(value) {
  const [source, entity] = String(value || '').split(':');
  return source && entity ? { source, entity } : null;
}

/** `zehn-hr:employee.full_name` → {source, path}; docs' own bindings → {source: 'docs', path}. */
export function splitBinding(binding) {
  const at = String(binding || '').indexOf(':');
  if (at <= 0) return null;
  return { source: binding.slice(0, at), path: binding.slice(at + 1) };
}

// docs' own bindings: filled when the document is registered in a journal.
export const DOCS_BINDINGS = ['docs:registration.number', 'docs:registration.date'];

/** Template language tag (uz-Latn, uz-Cyrl, ru, en, or a UI code) → the `lang` a module formats for. */
export function sourceLang(tag, uiLanguage) {
  const value = tag || uiLanguage || 'en';
  if (value === 'uz' || value === 'uz-Latn') return 'uz-Latn';
  if (value === 'uz-Cyrl' || value === 'ru' || value === 'en') return value;
  return 'en';
}

const withoutTrailingSlash = (url) => (url.endsWith('/') ? url.slice(0, -1) : url);

/** Calls to one module, as the person. `available` is false without a token for it. */
export function sourceApi(source, apiUrl) {
  const token = getModuleToken(source);
  const call = async (path, init = {}) => {
    const response = await fetch(`${withoutTrailingSlash(apiUrl)}/embed/v1/template-data${path}`, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw Object.assign(new Error(`${source} answered ${response.status}`), { status: response.status });
    }
    return response.json();
  };
  return {
    source,
    available: Boolean(token && apiUrl),
    catalogue: (lang) => call(`/catalogue?lang=${encodeURIComponent(lang || '')}`),
    search: (entity, q, limit = 20) =>
      call(`/${encodeURIComponent(entity)}?q=${encodeURIComponent(q || '')}&limit=${limit}`),
    values: (entity, id, paths, lang) =>
      call(`/${encodeURIComponent(entity)}/${encodeURIComponent(id)}/values`, {
        method: 'POST',
        body: JSON.stringify({ paths, lang }),
      }),
  };
}

/**
 * The API for `source`, or why it can't be used: `not_enabled` (the project
 * hasn't turned it on, or this deployment doesn't know it) or `no_token`
 * (docs wasn't opened from the office/portal that hands its token over).
 */
export async function resolveSource(source) {
  const sources = await fetchTemplateSources();
  const found = sources.find((s) => s.source === source);
  if (!found) return { reason: 'not_enabled', api: null };
  const api = sourceApi(source, found.apiUrl);
  return api.available ? { reason: null, api } : { reason: 'no_token', api: null };
}

/** Placeholders bound to `source`: [{key, path}]. */
export function boundToSource(items, source) {
  return (items || [])
    .map((item) => ({ key: item.key, bound: splitBinding(item.binding) }))
    .filter(({ bound }) => bound && bound.source === source)
    .map(({ key, bound }) => ({ key, path: bound.path }));
}

/** Test seam: forget the cached sources list. */
export function resetTemplateSourcesCache() {
  cached = null;
}
